/**
 * CommitRequestPanel — read-only review view shown when an artifact is in 'pending_commit'.
 * Replaces the editor tabs. See specs/phase2/CR-D-artifact-editing-frontend-spec.md §3.7.
 *
 * Role gates are UX-only — the backend re-enforces them:
 *   • Approve / Request Changes → PM or Leadership.
 *   • Withdraw → the original submitter, or admin / leadership.
 * Everyone else sees the panel in read-only mode with no action buttons.
 */

import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Clock, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  useApproveCommitRequest,
  useWithdrawCommitRequest,
  type ArtifactResponse,
} from "@/hooks/useArtifacts";
import { DiffViewer } from "./DiffViewer";
import { RejectDialog } from "./RejectDialog";

const REVIEWER_ROLES = ["pm", "leadership", "admin"];
const PRIVILEGED_ROLES = ["admin", "leadership"];

export interface CommitRequestPanelProps {
  artifact: ArtifactResponse;
  projectId: string;
  artifactType: string;
  userRole: string;
  onApproved: () => void;
  onRejected: () => void;
  onWithdrawn: () => void;
}

/** Format an ISO timestamp as e.g. "Aug 11, 2026 at 11:45 PM". Falls back to the raw value. */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const date = parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = parsed.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} at ${time}`;
}

export function CommitRequestPanel({
  artifact,
  projectId,
  artifactType,
  userRole,
  onApproved,
  onRejected,
  onWithdrawn,
}: CommitRequestPanelProps): JSX.Element {
  const { user } = useAuth();
  const [showDiff, setShowDiff] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const approve = useApproveCommitRequest(projectId, artifactType);
  const withdraw = useWithdrawCommitRequest(projectId, artifactType);

  const pending = artifact.pending_commit;

  const isReviewer = REVIEWER_ROLES.includes(userRole);
  const isPrivileged = PRIVILEGED_ROLES.includes(userRole);
  const isSubmitter =
    !!user && !!pending && user.userId === pending.requested_by;
  const canWithdraw = isSubmitter || isPrivileged;

  const busy = approve.isPending || withdraw.isPending;

  const handleApprove = async (): Promise<void> => {
    if (!pending) return;
    try {
      const result = await approve.mutateAsync(pending.commit_request_id);
      const shortSha = result.github_commit_sha.slice(0, 7);
      toast.success(`Committed to GitHub — SHA: ${shortSha}`);
      onApproved();
    } catch {
      // Shared axios interceptor surfaces the error toast.
    }
  };

  const handleWithdraw = async (): Promise<void> => {
    try {
      await withdraw.mutateAsync();
      toast.success("Commit request withdrawn — artifact returned to draft");
      onWithdrawn();
    } catch {
      // Shared axios interceptor surfaces the error toast.
    }
  };

  return (
    <div className="space-y-4">
      {/* Amber "pending approval" banner + metadata */}
      <div className="rounded-lg border border-warning/40 bg-badge-yellow-bg p-4">
        <div className="flex items-center gap-2 text-text-primary">
          <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold uppercase tracking-wide">
            Pending Approval
          </span>
        </div>

        {pending ? (
          <dl className="mt-3 space-y-1 text-sm text-text-primary">
            <div className="flex gap-2">
              <dt className="font-medium">Submitted by:</dt>
              <dd className="min-w-0 break-words">{pending.requested_by}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium">Submitted:</dt>
              <dd>{formatDateTime(pending.created_at)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium">Commit message:</dt>
              <dd className="min-w-0 break-words italic">
                &ldquo;{pending.commit_message}&rdquo;
              </dd>
            </div>
          </dl>
        ) : (
          <p className="mt-3 text-sm text-text-primary">
            A commit request is pending approval for this artifact.
          </p>
        )}
      </div>

      {/* Show Full Diff toggle */}
      {pending && (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDiff((v) => !v)}
            aria-expanded={showDiff}
          >
            {showDiff ? (
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            )}
            {showDiff ? "Hide Full Diff" : "Show Full Diff"}
          </Button>
          {showDiff && <DiffViewer diff={pending.diff} className="mt-3" />}
        </div>
      )}

      {/* Action buttons */}
      {(isReviewer || canWithdraw) && (
        <div className="flex flex-wrap items-center gap-2">
          {isReviewer && (
            <>
              <Button onClick={handleApprove} disabled={busy || !pending}>
                {approve.isPending && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                {approve.isPending ? "Committing…" : "Approve & Commit"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={busy || !pending}
              >
                Request Changes
              </Button>
            </>
          )}
          {canWithdraw && (
            <Button
              variant="ghost"
              onClick={handleWithdraw}
              disabled={busy}
              className={cn(isReviewer && "ml-auto")}
            >
              {withdraw.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
            </Button>
          )}
        </div>
      )}

      {pending && (
        <RejectDialog
          open={rejectOpen}
          projectId={projectId}
          artifactType={artifactType}
          reqId={pending.commit_request_id}
          onRejected={onRejected}
          onClose={() => setRejectOpen(false)}
        />
      )}
    </div>
  );
}

export default CommitRequestPanel;
